import React, { useState, useEffect, useRef } from 'react';
import { Layers, X } from 'lucide-react';
import { Reorder, AnimatePresence } from 'framer-motion';
import MapLayerItem from './MapLayerItem';
import ColorPicker from './ColorPicker';

const MapLayerList = ({
  layers,
  workspaceId,
  chatId,
  availableDatasets = [],
  onToggleVisibility,
  onRemoveLayer,
  onUpdateTheme,
  onAddLayer,
  onPreviewLayer,
  onOpenBuilder,
  onFilterLayer,
  onFlyToLayer,
  onReorderLayers
}) => {

  const [activePickerId, setActivePickerId] = useState(null);
  const [isMinimized, setIsMinimized] = useState(layers.length === 0);
  const prevLayerCount = useRef(layers.length);

  // Filter out the "preview-layer" which is only for map display, not the list
  const displayLayers = layers.filter(l => l.id !== 'preview-layer');
  const activeLayer = layers.find(l => l.id === activePickerId);

  // Auto-minimize when layers are removed (going from some to none)
  // Auto-expand when layers are added
  // But allow manual expansion even when empty
  useEffect(() => {
    // Auto-expand when a new layer is added
    if (prevLayerCount.current < displayLayers.length && displayLayers.length > 0) {
      setIsMinimized(false);
    }
    // Auto-minimize if we HAD layers and now have none
    else if (prevLayerCount.current > 0 && displayLayers.length === 0) {
      setIsMinimized(true);
    }

    prevLayerCount.current = displayLayers.length;
  }, [displayLayers.length]);

  const handleTogglePicker = (id) => {
    setActivePickerId(prev => prev === id ? null : id);
  };

  const handleRemove = (id) => {
    if (activePickerId === id) setActivePickerId(null);
    onRemoveLayer(id);
  };

  const handleReorder = (newDisplayOrder) => {
    if (!onReorderLayers) return;

    // We need to merge the new display order back with the hidden layers (like preview-layer)
    const previewLayer = layers.find(l => l.id === 'preview-layer');
    const newFullOrder = [...newDisplayOrder];
    if (previewLayer) {
      newFullOrder.push(previewLayer); // Preview layer always at the end/bottom/not reorderable
    }

    onReorderLayers(newFullOrder);
  };

  const activeLayersCount = displayLayers.filter(l => l.visible).length;
  const totalLayersCount = displayLayers.length;

  return (
    <div
      className="absolute top-6 left-6 z-[60]"
      onMouseMove={(e) => e.stopPropagation()}
      onMouseEnter={(e) => e.stopPropagation()}
    >
      {totalLayersCount === 0 ? null : isMinimized ? (
        // Minimized: Add button on top, layer button with badge below
        <div className="flex flex-col gap-2">
          {/* Add Layer button hidden */}
          <button
            onClick={() => setIsMinimized(false)}
            className="relative bg-white/50 dark:bg-gray-900/50 backdrop-blur-sm p-3 rounded-[16px] shadow-2xl border border-gray-100 dark:border-gray-800 transition-all duration-300 hover:bg-white/60 dark:hover:bg-gray-900/60 flex items-center justify-center cursor-pointer"
            title="Show Map Layers"
          >
            <Layers size={16} className="text-gray-600 dark:text-gray-400" />
            {totalLayersCount > 0 && (
              <div className="absolute -top-1 -right-1 min-w-[18px] h-[18px] bg-emerald-500 text-white text-[9px] font-black rounded-full flex items-center justify-center px-1">
                {activeLayersCount}
              </div>
            )}
          </button>
        </div>
      ) : (
        // Expanded: Full panel
        <div className="w-80 bg-white/50 dark:bg-gray-900/50 backdrop-blur-sm p-3 rounded-[24px] shadow-2xl border border-gray-100 dark:border-gray-800 transition-all duration-300 relative">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="p-1.5 text-gray-600 dark:text-gray-400 rounded-lg">
                <Layers size={14} />
              </div>
              <div>
                <h4 className="text-[10px] font-black text-gray-900 dark:text-white uppercase tracking-widest">
                  Map Layers
                </h4>
                <span className="text-[7px] font-bold text-gray-400 uppercase tracking-widest">
                  {activeLayersCount} / {totalLayersCount} Active
                </span>
              </div>
            </div>
            <div className="flex items-center gap-1">
              {/* Add Layer button hidden */}
              <button
                onClick={() => setIsMinimized(true)}
                className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                title="Minimize"
              >
                <X size={16} />
              </button>
            </div>
          </div>

          <div className="h-px bg-gray-200 dark:bg-gray-700 my-2" />
          <div className="max-h-[400px] overflow-y-auto pr-2 custom-scrollbar animate-in fade-in slide-in-from-top-2 duration-300">
            <Reorder.Group
              axis="y"
              values={displayLayers}
              onReorder={handleReorder}
              className="space-y-1.5 py-1"
            >
              <AnimatePresence initial={false}>
                {displayLayers.map((layer, index) => (
                  <Reorder.Item
                    key={layer.id}
                    value={layer}
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    transition={{ duration: 0.2 }}
                  >
                    {index > 0 && (
                      <div className="h-px bg-gray-200 dark:bg-gray-700 mb-1.5" />
                    )}
                    <MapLayerItem
                      layer={layer}
                      isActive={activePickerId === layer.id}
                      onToggleVisibility={onToggleVisibility}
                      onRemove={handleRemove}
                      onUpdateTheme={onUpdateTheme}
                      onTogglePicker={handleTogglePicker}
                      onFilterLayer={onFilterLayer}
                      onFlyToLayer={onFlyToLayer}
                      isDraggable={true}
                    />
                  </Reorder.Item>
                ))}
              </AnimatePresence>
              {displayLayers.length === 0 && (
                <div className="text-center py-4">
                  <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest">No active layers</p>
                </div>
              )}
            </Reorder.Group>
          </div>

          {/* Color Picker rendered outside scrollable area but inside panel or absolute to it */}
          {activeLayer && (
            <div className="absolute left-[calc(100%+12px)] top-0">
              <ColorPicker
                layer={activeLayer}
                onClose={() => setActivePickerId(null)}
                onUpdateTheme={onUpdateTheme}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MapLayerList;
